// Estilos da tela de Dashboard (/).
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Dashboard.dc.html
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

// Colunas do card de atendentes, na ordem do mockup.
export const AGENTS_GRID =
  "minmax(0,1.5fr) 92px 104px 112px 112px minmax(140px,1fr) 116px";

const useDashboardPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderStrong = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.14)";

  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },

    // Segmentado Hoje / 7 dias / 30 dias / Personalizado
    rangeGroup: {
      display: "flex",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"
      }`,
      borderRadius: 10,
      padding: 3,
      gap: 2,
    },
    rangeButton: {
      padding: "5px 12px",
      borderRadius: 8,
      fontSize: 12.5,
      fontWeight: 600,
      minWidth: "auto",
      textTransform: "none",
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
    },
    rangeButtonActive: {
      backgroundColor: alpha(AEROS.defaultPrimary, 0.2),
      color: theme.palette.text.primary,
    },

    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 10,
    },

    // ---- indicadores ----
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5, minmax(0,1fr))",
      gap: 12,
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      },
    },
    kpiCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      minWidth: 0,
    },
    // O primeiro card do mockup tem a borda destacada.
    kpiCardAccent: { borderColor: alpha(AEROS.defaultPrimary, 0.3) },
    kpiTop: { display: "flex", alignItems: "center", gap: 8 },
    kpiIcon: {
      width: 30,
      height: 30,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    kpiDelta: {
      marginLeft: "auto",
      fontSize: 11,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 2,
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    kpiValue: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 24,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      lineHeight: 1.15,
    },
    kpiLabel: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.35,
    },

    // ---- linha gráfico + NPS ----
    chartRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr)",
      gap: 16,
      alignItems: "start",
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: "18px 20px",
      minWidth: 0,
    },
    cardFlush: { padding: 0, overflow: "hidden" },
    cardHead: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      flexWrap: "wrap",
    },
    cardTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    cardSub: { fontSize: 12, color: theme.palette.text.secondary },
    legend: {
      marginLeft: "auto",
      display: "flex",
      gap: 12,
      fontSize: 11.5,
      color: theme.palette.text.secondary,
    },
    legendItem: { display: "flex", alignItems: "center", gap: 5 },
    legendDot: { width: 9, height: 9, borderRadius: 3, flex: "none" },

    // barras
    chart: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "flex-start",
      gap: 10,
      height: 180,
    },
    // maxWidth: com 2 ou 3 dias no período, `flex: 1` transformaria cada dia
    // num bloco de 300px de largura.
    chartCol: {
      flex: 1,
      maxWidth: 96,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    chartBars: {
      display: "flex",
      alignItems: "flex-end",
      gap: 3,
      width: "100%",
      height: 150,
    },
    // altura zero ainda desenha 2px, senão o dia sem dado some do gráfico
    bar: { flex: 1, borderRadius: "5px 5px 2px 2px", minHeight: 2 },
    barIn: { backgroundColor: alpha(AEROS.defaultPrimary, 0.55) },
    barOut: { backgroundColor: alpha(AEROS.cyan, 0.6) },
    chartDay: {
      fontSize: 10,
      color: theme.palette.aeros.textTertiary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },
    chartEmpty: {
      height: 180,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },

    // ---- NPS ----
    npsBody: { display: "flex", alignItems: "center", gap: 16 },
    donut: {
      width: 104,
      height: 104,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
    },
    donutHole: {
      width: 76,
      height: 76,
      borderRadius: "50%",
      backgroundColor: theme.palette.aeros.surface,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    donutValue: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.1,
    },
    donutLabel: {
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    npsList: { flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 },
    npsRow: { display: "flex", alignItems: "baseline", fontSize: 12 },
    npsName: { color: theme.palette.text.primary },
    npsPct: {
      marginLeft: "auto",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
    },
    track: {
      height: 6,
      borderRadius: 99,
      backgroundColor: isLight ? "rgba(15,20,40,0.07)" : "rgba(255,255,255,0.07)",
      marginTop: 4,
      overflow: "hidden",
    },
    trackFill: { height: "100%", borderRadius: 99 },

    ratingBlock: {
      borderTop: `1px solid ${border}`,
      paddingTop: 12,
      marginTop: 14,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    ratingTitle: {
      fontSize: 12,
      fontWeight: 700,
      color: theme.palette.text.secondary,
    },
    ratingRow: { display: "flex", alignItems: "center", gap: 10 },
    ratingTrack: {
      flex: 1,
      height: 8,
      borderRadius: 99,
      backgroundColor: isLight ? "rgba(15,20,40,0.07)" : "rgba(255,255,255,0.07)",
      overflow: "hidden",
    },
    ratingFill: {
      height: "100%",
      borderRadius: 99,
      background: `linear-gradient(90deg, ${AEROS.warning}, ${AEROS.success})`,
    },
    ratingPct: {
      fontSize: 14,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: stateText(theme, AEROS.warning, "warning"),
    },
    ratingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      gap: 8,
      marginTop: 2,
    },
    ratingTile: {
      backgroundColor: isLight ? "rgba(15,20,40,0.03)" : theme.palette.aeros.sidebar,
      borderRadius: 9,
      padding: "8px 10px",
      minWidth: 0,
    },
    ratingTileValue: {
      fontSize: 15,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    ratingTileLabel: {
      fontSize: 10.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
    },

    // ---- atendentes ----
    agentsHead: {
      display: "flex",
      alignItems: "center",
      padding: "14px 18px 12px",
      gap: 10,
      borderBottom: `1px solid ${border}`,
      flexWrap: "wrap",
    },
    agentsLink: {
      marginLeft: "auto",
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "none",
      color: stateText(theme, AEROS.cyan, "info"),
    },
    // Em tela estreita o grid de agentes (~780px de colunas fixas) rola dentro
    // do card, em vez de ser cortado pelo overflow hidden do cardFlush — antes
    // as colunas STATUS/AVALIACAO/T.M. ESPERA simplesmente sumiam em 375/768px.
    agentsScroll: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
    agentsRowHead: {
      display: "grid",
      minWidth: 780,
      gridTemplateColumns: AGENTS_GRID,
      gap: 10,
      padding: "9px 18px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    agentsRow: {
      display: "grid",
      minWidth: 780,
      gridTemplateColumns: AGENTS_GRID,
      gap: 10,
      padding: "11px 18px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      alignItems: "center",
      fontSize: 13,
      "&:last-child": { borderBottom: "none" },
    },
    agentCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    agentAvatar: {
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11.5,
      fontWeight: 700,
      flex: "none",
      color: isLight ? "#FFFFFF" : "#07080D",
    },
    agentName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    agentQueue: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    num: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    numMuted: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
    },
    headRight: { textAlign: "right" },
    headCenter: { textAlign: "center" },
    agentRating: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    agentRatingValue: {
      fontSize: 12,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      flex: "none",
    },
    statusCell: { display: "flex", justifyContent: "center" },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      whiteSpace: "nowrap",
    },
    statusDot: { width: 6, height: 6, borderRadius: "50%", flex: "none" },
    emptyRow: {
      padding: "24px 18px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },

    // ---- popover de filtros ----
    // O tema (aerosTheme) força `width: auto !important` e
    // `max-width: 300px !important` em TODO MuiPopover — pensado para menus.
    // Este popover é um formulário, então precisa vencer com !important
    // também; estilo inline sozinho não vence um !important.
    filterPaper: {
      width: "420px !important",
      maxWidth: "calc(100vw - 32px) !important",
      marginTop: 8,
      backgroundColor: theme.palette.aeros.surface2,
      border: `1px solid ${borderStrong}`,
      borderRadius: 16,
      padding: 20,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    filterTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    segmented: {
      display: "flex",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"
      }`,
      borderRadius: 10,
      padding: 3,
    },
    segment: {
      flex: 1,
      textAlign: "center",
      whiteSpace: "nowrap",
      padding: "6px 8px",
      borderRadius: 8,
      fontSize: 12.5,
      fontWeight: 600,
      textTransform: "none",
      color: theme.palette.text.secondary,
      minWidth: 0,
    },
    segmentActive: {
      backgroundColor: alpha(AEROS.defaultPrimary, 0.2),
      color: theme.palette.text.primary,
    },
    filterGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      gap: 10,
    },
    filterActions: { display: "flex", gap: 10, justifyContent: "flex-end" },
  };
});

export default useDashboardPageStyles;
